import type { WhatMattersNow } from "@/data/portal/intelligence";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  AlertTriangle, CheckCircle, ChevronRight, Circle, Info, Shield,
} from "lucide-react";

interface Props {
  data: WhatMattersNow;
  onActionClick?: (actionId: string) => void;
}

const CONFIDENCE_CONFIG = {
  high: { label: "On Track", icon: CheckCircle, class: "text-success" },
  medium: { label: "Needs Attention", icon: Info, class: "text-warning" },
  low: { label: "At Risk", icon: AlertTriangle, class: "text-destructive" },
} as const;

const URGENCY_CONFIG = {
  critical: { class: "border-destructive/30 bg-destructive/5" },
  attention: { class: "border-warning/30 bg-warning/5" },
  info: { class: "border-primary/20 bg-primary/5" },
  none: { class: "border-border/40 bg-card" },
} as const;

export function WhatMattersNowCard({ data, onActionClick }: Props) {
  const conf = CONFIDENCE_CONFIG[data.confidence];
  const ConfIcon = conf.icon;
  const urgencyStyle = URGENCY_CONFIG[data.urgency];

  return (
    <div className={cn("rounded-2xl border p-5 space-y-4", urgencyStyle.class)}>
      {/* Status + Confidence */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 flex-1 min-w-0">
          <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
            What Matters Now
          </p>
          <h2 className="text-lg font-bold text-foreground leading-tight">
            {data.currentStatus}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {data.nextStep}
          </p>
        </div>
        <div className={cn("flex items-center gap-1.5 shrink-0 px-3 py-1.5 rounded-full bg-background/80 border border-border/40", conf.class)}>
          <ConfIcon className="h-3.5 w-3.5" />
          <span className="text-xs font-semibold">{conf.label}</span>
        </div>
      </div>

      {/* Blocking Issues */}
      {data.blockingIssues.length > 0 && (
        <div className="space-y-1.5">
          {data.blockingIssues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-destructive/5 border border-destructive/10">
              <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
              <p className="text-xs text-foreground leading-relaxed">{issue}</p>
            </div>
          ))}
        </div>
      )}

      {/* Actions Required */}
      {data.confirmationsRequired.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Needs Your Attention</p>
          {data.confirmationsRequired.map(action => (
            <button
              key={action.id}
              onClick={() => onActionClick?.(action.id)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-background border border-border/40 hover:border-primary/30 hover:bg-primary/5 transition-all text-left group"
            >
              <div className={cn(
                "h-8 w-8 rounded-lg flex items-center justify-center shrink-0",
                action.urgency === "critical" ? "bg-destructive/10" :
                action.urgency === "attention" ? "bg-warning/10" : "bg-primary/10"
              )}>
                {action.urgency === "critical" ? (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                ) : action.urgency === "attention" ? (
                  <Circle className="h-4 w-4 text-warning" />
                ) : (
                  <Info className="h-4 w-4 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">{action.label}</p>
                <p className="text-xs text-muted-foreground truncate">{action.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
            </button>
          ))}
        </div>
      )}

      {/* Confidence Detail */}
      {data.confidence !== "high" && data.confidenceReasons.length > 0 && (
        <div className="flex items-start gap-2 pt-1">
          <Shield className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {data.confidenceReasons.join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}
